import { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { useAtomValue } from "jotai";
import { userState } from "@/lib/store";
import { Stock } from "@/models/stock";
import { afterStartup, currentConnection, preloadPlan, trackApiInflight, whenApiQuiet } from "@/lib/tab-preload";
import { DEFAULT_SHORTS_TIME_RANGE, preloadShortInterestTab, SHORTS_TIME_RANGE_SETTING } from "@/lib/short-interest-preload";

// Count API requests from the moment the overview's code loads, before its own effects send any.
if (typeof window !== "undefined") trackApiInflight();

/**
 * On a stock overview, preloads the short-interest tab once the overview has finished starting up
 * (ortex/ortex-gui#1350): after `window.__ocAfterStartup`, once the user request has answered, and
 * once the page's API requests have been quiet for half a second. Skipped on data-saver and 2G
 * connections; the API warm-up is for signed-in readers only. Once per stock per page view.
 */
const useShortInterestPreload = (stock: Stock | undefined) => {
    const router = useRouter();
    const user = useAtomValue(userState);
    const plan = user?.subscription_plan;
    const savedRange: string | undefined = user?.settings?.module_settings?.[SHORTS_TIME_RANGE_SETTING]?.value;
    const done = useRef<number | null>(null);

    useEffect(() => {
        const tid = stock?.tradingitem_id;
        if (!tid || !plan || done.current === tid) return;
        let cancelled = false;
        afterStartup(() => {
            if (cancelled) return;
            whenApiQuiet().then((quiet) => {
                if (cancelled || !quiet || done.current === tid) return;
                const p = preloadPlan(currentConnection(), plan);
                if (!p) return;
                done.current = tid;
                preloadShortInterestTab(router, stock, p, savedRange || DEFAULT_SHORTS_TIME_RANGE);
            });
        });
        return () => {
            cancelled = true;
        };
        // The router object is stable for the page; stock and plan decide when to run.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stock?.tradingitem_id, plan]);
};

export default useShortInterestPreload;
